/**
 * API client for HSE incidents (DEV-822 / Phase 2).
 *
 * Wraps the backend endpoint that lists project_issues with
 * `subtype='hse_incident'`. Used by the HSE row to compute card scores.
 *
 * Backend endpoint (not yet implemented at time of this writing — see
 * DEV-826): `GET /api/projects/{projectGuid}/hse-incidents` with optional
 * query params for category and period. Until that ships, the hook below
 * gracefully returns an empty array so the HSE cards still render in
 * their clean state.
 *
 * When DEV-826 lands, only the `fetchHseIncidents` body needs to swap
 * from the placeholder to a real fetch. The shape of `HseIncident` and
 * the hook contract are stable.
 */

import { useEffect, useMemo, useState } from 'react';
import type { HseIncident } from '../kpi-engine/hse';
import { apiFetch, ApiError } from './client';
import { fetchForKey, CACHE_UPDATE_EVENT } from './queryCache';

/* ------------------------------------------------------------------------- */
/*  Backend response shape — ASP.NET serializes properties as camelCase by   */
/*  default. Mirrors ProjectIssueResponse with HSE columns populated.        */
/* ------------------------------------------------------------------------- */

interface RawHseIncidentResponse {
  id: string;
  title?: string;
  description?: string;
  status?: string;
  hseCategory?: string;
  severityHse?: string;
  incidentDate?: string;
  confidential?: boolean;
  subtype?: string;
}

function rawToIncident(raw: RawHseIncidentResponse): HseIncident | null {
  // Defensive: skip rows with missing required HSE fields rather than
  // crashing the engine. Logs a warning so we can spot bad data.
  if (!raw.hseCategory || !raw.severityHse || !raw.incidentDate) {
    console.warn('[hse] dropping malformed incident from API:', raw);
    return null;
  }
  return {
    issue_id: raw.id,
    incident_date: raw.incidentDate.slice(0, 10), // strip time, keep ISO date
    hse_category: raw.hseCategory as HseIncident['hse_category'],
    severity: raw.severityHse as HseIncident['severity'],
    title: raw.confidential ? undefined : raw.title,
    status: raw.status,
    confidential: raw.confidential ?? false,
  };
}

/* ------------------------------------------------------------------------- */
/*  Fetcher                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Fetch all HSE incidents for a project. Returns an empty array if the
 * backend endpoint is not yet implemented (graceful degradation during
 * the rollout window).
 */
export async function fetchHseIncidents(
  projectGuid: string,
): Promise<HseIncident[]> {
  if (!projectGuid) return [];

  try {
    const path = `/api/projects/${encodeURIComponent(projectGuid)}/hse-incidents`;
    const data = await apiFetch<RawHseIncidentResponse[]>(path, {
      method: 'GET',
    });
    if (!Array.isArray(data)) return [];
    return data
      .map(rawToIncident)
      .filter((x): x is HseIncident => x !== null);
  } catch (err) {
    // 404 = endpoint not yet deployed (DEV-826 still pending). Network
    // failure or backend down also lands here. Degrade quietly so the
    // HSE cards still render in their clean state during rollout.
    if (err instanceof ApiError && err.status === 404) {
      return [];
    }
    console.warn('[hse] fetchHseIncidents failed:', err);
    return [];
  }
}

/* ------------------------------------------------------------------------- */
/*  React hook                                                               */
/* ------------------------------------------------------------------------- */

export interface UseHseIncidentsResult {
  incidents: HseIncident[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const HSE_CACHE_KEY_PREFIX = 'hse-incidents:';

/**
 * Subscribe to the HSE incidents for a project, with cache-update event
 * invalidation. Mirrors the pattern used by `useApiMissionData`.
 */
export function useHseIncidents(projectGuid: string): UseHseIncidentsResult {
  const [state, setState] = useState<{
    incidents: HseIncident[];
    loading: boolean;
    error: string | null;
  }>({ incidents: [], loading: true, error: null });

  const refetch = async () => {
    if (!projectGuid) {
      setState({ incidents: [], loading: false, error: null });
      return;
    }
    try {
      const cacheKey = HSE_CACHE_KEY_PREFIX + projectGuid;
      const incidents = await fetchForKey<HseIncident[]>(
        cacheKey,
        () => fetchHseIncidents(projectGuid),
        { force: false, fallbackFn: () => [] },
      );
      setState({ incidents: incidents ?? [], loading: false, error: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({ incidents: [], loading: false, error: msg });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    refetch().then(() => {
      if (cancelled) return;
    });

    const handler = () => {
      if (!cancelled) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(CACHE_UPDATE_EVENT, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectGuid]);

  return { ...state, refetch };
}

/* ------------------------------------------------------------------------- */
/*  Mini-Project variant (DEV-1141)                                          */
/*                                                                            */
/*  Same incident shape, same graceful-degradation contract, but reads from  */
/*  the polymorphic mini-project route. Kept as a sibling fetcher/hook rather */
/*  than overloading useHseIncidents so the per-project call sites stay       */
/*  byte-identical. Separate cache prefix so the two owners never collide.    */
/* ------------------------------------------------------------------------- */

const HSE_MP_CACHE_KEY_PREFIX = 'hse-mp-incidents:';

/**
 * Fetch all HSE incidents for a mini-project. Returns an empty array on 404
 * or network failure so the HSE cards still render in their clean state.
 */
export async function fetchHseMiniProjectIncidents(
  miniProjectGuid: string,
): Promise<HseIncident[]> {
  if (!miniProjectGuid) return [];

  try {
    const path = `/api/mini-projects/${encodeURIComponent(miniProjectGuid)}/hse-incidents`;
    const data = await apiFetch<RawHseIncidentResponse[]>(path, { method: 'GET' });
    if (!Array.isArray(data)) return [];
    return data
      .map(rawToIncident)
      .filter((x): x is HseIncident => x !== null);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return [];
    }
    console.warn('[hse] fetchHseMiniProjectIncidents failed:', err);
    return [];
  }
}

/**
 * Subscribe to the HSE incidents for a mini-project, with cache-update
 * event invalidation. Mirrors {@link useHseIncidents} exactly.
 */
export function useHseMiniProjectIncidents(
  miniProjectGuid: string,
): UseHseIncidentsResult {
  const [state, setState] = useState<{
    incidents: HseIncident[];
    loading: boolean;
    error: string | null;
  }>({ incidents: [], loading: true, error: null });

  const refetch = async () => {
    if (!miniProjectGuid) {
      setState({ incidents: [], loading: false, error: null });
      return;
    }
    try {
      const cacheKey = HSE_MP_CACHE_KEY_PREFIX + miniProjectGuid;
      const incidents = await fetchForKey<HseIncident[]>(
        cacheKey,
        () => fetchHseMiniProjectIncidents(miniProjectGuid),
        { force: false, fallbackFn: () => [] },
      );
      setState({ incidents: incidents ?? [], loading: false, error: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({ incidents: [], loading: false, error: msg });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    refetch().then(() => {
      if (cancelled) return;
    });

    const handler = () => {
      if (!cancelled) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(CACHE_UPDATE_EVENT, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniProjectGuid]);

  return { ...state, refetch };
}

/* ------------------------------------------------------------------------- */
/*  Bulk fetch (DEV-839) — many projects in one round trip                   */
/*                                                                            */
/*  Used by App.tsx to attach `hse_bundle` to every project row in the       */
/*  visibleProjects memo, so KpiGridTable shows HSE-capped colors with no    */
/*  N+1 fetch pattern. Mirrors the per-project hook's graceful degradation.  */
/* ------------------------------------------------------------------------- */

interface RawBulkResponse {
  [projectGuid: string]: RawHseIncidentResponse[];
}

const HSE_BULK_CACHE_KEY_PREFIX = 'hse-incidents-bulk:';

/**
 * Fetch HSE incidents for many projects. Returns a Map keyed by
 * projectGuid (lowercase) → incidents[]. Projects with no incidents are
 * present in the map with an empty array. Backend-side hard cap is 200
 * GUIDs per request; we mirror that here as a defensive guard.
 */
export async function fetchHseIncidentsBulk(
  projectGuids: string[],
): Promise<Map<string, HseIncident[]>> {
  const cleaned = Array.from(
    new Set(projectGuids.map((g) => String(g || '').trim()).filter(Boolean)),
  );
  if (cleaned.length === 0) return new Map();
  if (cleaned.length > 200) {
    // Backend would reject; fail loud here instead of confusing partial data.
    console.warn(`[hse:bulk] truncating ${cleaned.length} guids to 200 (server cap)`);
    cleaned.length = 200;
  }

  try {
    const data = await apiFetch<RawBulkResponse>('/api/hse-incidents/bulk', {
      method: 'POST',
      body: JSON.stringify({ projectGuids: cleaned }),
    });

    const result = new Map<string, HseIncident[]>();
    if (!data || typeof data !== 'object') return result;
    for (const [guid, raws] of Object.entries(data)) {
      const incidents = (Array.isArray(raws) ? raws : [])
        .map(rawToIncident)
        .filter((x): x is HseIncident => x !== null);
      // Normalize key to lowercase so consumers don't have to think about case.
      result.set(guid.toLowerCase(), incidents);
    }
    // Ensure every requested guid has an entry, even if backend omitted it
    // (defensive — current backend always populates, but contract allows
    // either shape and consumers shouldn't have to check both).
    for (const g of cleaned) {
      const k = g.toLowerCase();
      if (!result.has(k)) result.set(k, []);
    }
    return result;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Endpoint not deployed yet — return empty map so the UI degrades
      // exactly like the per-project hook (no floor cap, but no errors).
      return new Map();
    }
    console.warn('[hse:bulk] fetchHseIncidentsBulk failed:', err);
    return new Map();
  }
}

export interface UseHseIncidentsBulkResult {
  /** Map keyed by lowercase projectGuid → incidents[]. Empty Map while loading. */
  incidentsByProject: Map<string, HseIncident[]>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Subscribe to HSE incidents for many projects. Invalidates on
 * CACHE_UPDATE_EVENT so SignalR pushes refresh the bulk view. Cache key
 * is derived from the SORTED guid list so reordering doesn't bust the
 * cache, but adding/removing a project does.
 */
export function useHseIncidentsBulk(
  projectGuids: string[],
): UseHseIncidentsBulkResult {
  // Stable cache key — sort guids so reorder doesn't invalidate. We do
  // NOT include length-based shortcuts because adding a project must
  // re-fetch.
  const sortedGuids = useMemo(
    () =>
      Array.from(
        new Set(projectGuids.map((g) => String(g || '').trim().toLowerCase()).filter(Boolean)),
      ).sort(),
    [projectGuids],
  );
  const cacheKey = HSE_BULK_CACHE_KEY_PREFIX + sortedGuids.join(',');

  const [state, setState] = useState<{
    incidentsByProject: Map<string, HseIncident[]>;
    loading: boolean;
    error: string | null;
  }>({ incidentsByProject: new Map(), loading: true, error: null });

  const refetch = async () => {
    if (sortedGuids.length === 0) {
      setState({ incidentsByProject: new Map(), loading: false, error: null });
      return;
    }
    try {
      const incidentsByProject = await fetchForKey<Map<string, HseIncident[]>>(
        cacheKey,
        () => fetchHseIncidentsBulk(sortedGuids),
        { force: false, fallbackFn: () => new Map() },
      );
      setState({
        incidentsByProject: incidentsByProject ?? new Map(),
        loading: false,
        error: null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({ incidentsByProject: new Map(), loading: false, error: msg });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    refetch().then(() => {
      if (cancelled) return;
    });
    const handler = () => {
      if (!cancelled) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(CACHE_UPDATE_EVENT, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return { ...state, refetch };
}
